import { ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { errorMessage, operatorApi, useFetch } from '../../services/operatorApi';
import { Badge, Button, Card, DocumentStatusBadge, Empty, ErrorBox, PageHeader, SignerStatusBadge, Spinner, Table, Td, dateTime, num } from '../../components/ui';

interface Detail {
  document: {
    id: string; title: string; external_ref: string | null; status: string; created_at: string; sent_at: string | null; completed_at: string | null;
    expires_at: string | null; voided_at: string | null; void_reason: string | null; signature_style: string; signature_pages_added: number;
    hash_original: string | null; hash_sealed: string | null; uploaded_sha256: string | null; pades_level: string | null; client_id: string; client: string;
  };
  signers: { id: string; order_index: number; full_name: string; email: string; status: string; otp_channel: string; signed_at: string | null; declined_at: string | null; decline_reason: string | null; signature_mode: string | null; timestamp_provider: string | null }[];
  events: { type: string; occurred_at: string; signer_id: string | null }[];
  credits_consumed: number;
}

const STYLE_LABELS: Record<string, string> = { draw_or_type: 'Tracer ou taper', draw: 'Tracé imposé', type: 'Nom saisi imposé', none: 'Invisible' };

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 py-2.5 sm:flex-row sm:gap-4">
      <dt className="w-44 shrink-0 text-sm text-slate-500">{label}</dt>
      <dd className="min-w-0 break-all text-sm text-slate-900">{children}</dd>
    </div>
  );
}

export default function DocumentDetailPage() {
  const { id } = useParams();
  const { data, error, loading, reload } = useFetch(async () => (await operatorApi.get<Detail>(`/documents/${id}`)).data, [id]);
  const [sealing, setSealing] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  if (loading && !data) return <Spinner />;
  if (error && !data) return <ErrorBox message={error} onRetry={reload} />;
  if (!data) return null;
  const { document: doc, signers, events } = data;

  const allSigned = signers.length > 0 && signers.every((s) => s.status === 'signed');
  const canReseal = allSigned && doc.status !== 'completed';

  const reseal = async () => {
    setSealing(true);
    setMessage(null);
    try {
      const response = await operatorApi.post<{ sealed: boolean }>(`/documents/${doc.id}/seal`);
      setMessage({ ok: response.data.sealed, text: response.data.sealed ? 'Document scellé.' : 'Le scellement n’a pas abouti : consultez les journaux de l’API.' });
      await reload();
    } catch (e) {
      setMessage({ ok: false, text: errorMessage(e) });
    } finally {
      setSealing(false);
    }
  };

  return (
    <div>
      <Link to="/documents" className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4" />
        Documents
      </Link>
      <PageHeader
        title={doc.title}
        subtitle={`Client : ${doc.client}`}
        actions={
          <>
            <DocumentStatusBadge status={doc.status} />
            {canReseal && (
              <Button onClick={reseal} loading={sealing}>
                <ShieldCheck className="w-4 h-4" />
                Relancer le scellement
              </Button>
            )}
          </>
        }
      />
      {message && <div className="mb-4"><ErrorBox message={message.text} /></div>}
      {canReseal && !message && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Tous les signataires ont signé mais le document n&apos;est pas scellé. Relancez le scellement.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card title="Signataires" className="xl:col-span-2">
          <Table head={['#', 'Signataire', 'Statut', 'Canal', 'Apparence', 'Signé le']}>
            {signers.map((s) => (
              <tr key={s.id}>
                <Td className="tabular-nums text-slate-400">{s.order_index + 1}</Td>
                <Td>
                  <div className="font-medium text-slate-900">{s.full_name}</div>
                  <div className="text-xs text-slate-500">{s.email}</div>
                </Td>
                <Td>
                  <SignerStatusBadge status={s.status} />
                  {s.decline_reason && <div className="mt-1 text-xs text-slate-500">« {s.decline_reason} »</div>}
                </Td>
                <Td>{s.otp_channel === 'email_sms' ? <Badge tone="violet">E-mail + code SMS</Badge> : s.otp_channel === 'sms' ? <Badge tone="violet">SMS</Badge> : <Badge>E-mail</Badge>}</Td>
                <Td className="text-slate-500">{s.signature_mode === 'drawn' ? 'Tracé' : s.signature_mode === 'typed' ? 'Nom saisi' : '—'}</Td>
                <Td className="whitespace-nowrap text-slate-500">{dateTime(s.signed_at)}</Td>
              </tr>
            ))}
          </Table>
        </Card>

        <Card title="Informations">
          <dl className="divide-y divide-slate-100">
            <Row label="Identifiant"><span className="font-mono text-xs">{doc.id}</span></Row>
            {doc.external_ref && <Row label="Référence client">{doc.external_ref}</Row>}
            <Row label="Client"><Link className="text-indigo-600 hover:underline" to={`/clients/${doc.client_id}`}>{doc.client}</Link></Row>
            <Row label="Créé">{dateTime(doc.created_at)}</Row>
            <Row label="Envoyé">{dateTime(doc.sent_at)}</Row>
            <Row label="Terminé">{dateTime(doc.completed_at)}</Row>
            <Row label="Expire">{dateTime(doc.expires_at)}</Row>
            <Row label="Apparence">{STYLE_LABELS[doc.signature_style] ?? doc.signature_style}{doc.signature_pages_added > 0 ? ` · ${doc.signature_pages_added} page ajoutée` : ''}</Row>
            <Row label="Niveau PAdES">{doc.pades_level ?? '—'}</Row>
            <Row label="Crédits consommés">{num(data.credits_consumed)}</Row>
            {doc.void_reason && <Row label="Motif d'annulation">{doc.void_reason}</Row>}
          </dl>
        </Card>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card title="Journal des événements" className="xl:col-span-2">
          {events.length === 0 ? (
            <Empty>Aucun événement.</Empty>
          ) : (
            <ul className="space-y-2">
              {events.map((e, index) => {
                const signer = signers.find((s) => s.id === e.signer_id);
                return (
                  <li key={index} className="flex items-baseline justify-between gap-4 text-sm">
                    <span><span className="font-mono text-xs text-slate-700">{e.type}</span>{signer && <span className="text-slate-500"> · {signer.full_name}</span>}</span>
                    <span className="whitespace-nowrap text-xs text-slate-500">{dateTime(e.occurred_at)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
        <Card title="Empreintes SHA-256">
          <dl className="divide-y divide-slate-100">
            <Row label="PDF reçu"><span className="font-mono text-xs">{doc.uploaded_sha256 ?? '—'}</span></Row>
            <Row label="PDF présenté"><span className="font-mono text-xs">{doc.hash_original ?? '—'}</span></Row>
            <Row label="PDF scellé"><span className="font-mono text-xs">{doc.hash_sealed ?? '—'}</span></Row>
          </dl>
        </Card>
      </div>
    </div>
  );
}
